import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react"
import anime from "animejs"
import classNames from "classnames"
import dayjs from "dayjs"
import { AnimatePresence, motion, usePresence } from "motion/react"

import { useETL, useTranslation } from "@/locales"
import { GlobalContext } from "@/topic/rogue_5/context"
import { useTopicI18nStore } from "@/topic/rogue_5/locales"
import { getStartOfDay } from "@/topic/rogue_5/utils/misc"
import { SFX } from "@/topic/rogue_5/utils/sfx"
import { getRoguelikeItem } from "@/utils/assets"
import { isMobile } from "@/utils/misc"
import MainButton from "../../components/MainButton"
import { useLotteryModal } from "../../components/Modal/LotteryModal"
import { useShareModal } from "../../components/Modal/ShareModal"
import { BackButton } from "../../components/RightButton"
import { drawShareImage } from "../../share"
import { EGameScene, useGameStore } from "../../store"

import styles from "./index.module.less"

const SCENE_ANIMATION = {
    animate: async (container: HTMLDivElement) => {
        const buttonGroup = container.querySelector(
            `.${styles.buttonGroup}`
        ) as HTMLDivElement
        return anime({
            targets: buttonGroup,
            opacity: [0, 1],
            translateY: ["50%", "0"],
            translateX: ["-50%", "-50%"],
            duration: 1000,
            delay: 1200,
            easing: "easeOutCubic",
            begin: () => {
                SFX.play("resultIn")
            },
            complete: () => {
                buttonGroup.style.pointerEvents = "auto"
            },
        }).finished
    },
    exit: async (container: HTMLDivElement) => {
        return anime({
            targets: container,
            opacity: [1, 0],
            filter: ["blur(0px)", "blur(10px)"],
            scale: [1, 0.9],
            duration: 1000,
            easing: "easeInCubic",
        }).finished
    },
}
const GameSceneResult: React.FC<{
    className?: string
    style?: React.CSSProperties
}> = ({ className, style }) => {
    const [showButton, setShowButton] = useState(false)
    const {
        state: { user },
    } = useContext(GlobalContext)
    const { t } = useTranslation()
    const {
        objects: { relicDict: RELIC_DICT },
    } = useTopicI18nStore()
    const [isPresent, safeToRemove] = usePresence()
    const containerRef = useRef<HTMLDivElement>(null)
    useEffect(() => {
        if (!containerRef.current) return
        if (isPresent) {
            SCENE_ANIMATION.animate(containerRef.current).then(() => {
                setShowButton(true)
            })
        } else {
            setShowButton(false)
            SCENE_ANIMATION.exit(containerRef.current).then(() => {
                // console.log("exit")
                safeToRemove()
            })
        }
    }, [isPresent])

    const {
        gameResult,
        gameType,
        setScene,
        info: gameInfo,
        gameRandomResult,
        resetGameResult,
        canRoll,
    } = useGameStore()
    const [src, setSrc] = useState<string>("")
    // useEffect(() => {
    //     rowGameResult()
    // }, [])
    const needSignIn = useMemo(() => {
        const signInRecord = gameInfo.signInRecord
        if (signInRecord.length >= 7) return false
        const lastSignIn =
            signInRecord.slice().sort((a, b) => b.ts - a.ts)[0]?.ts ?? 0
        if (lastSignIn) {
            const startOfDay = getStartOfDay()
            if (lastSignIn < startOfDay) {
                return true
            } else {
                return false
            }
        }
        return true
    }, []) // 只在初始化时执行一次
    useEffect(() => {
        if (gameResult && gameType) {
            const draw = async () => {
                const imageSrc = await drawShareImage({
                    type: gameType,
                    coins: gameResult.coins,
                    result: gameResult.result,
                    doctorName: user?.nickName || "",
                    uid: user?.uid || "",
                    randomResult: gameRandomResult,
                })
                setSrc(imageSrc)
            }
            // setTimeout(() => {
            draw()
            // }, 1000)
        }
    }, [gameResult, gameType, user, gameRandomResult])

    const callShareModal = useShareModal()
    const callLotteryModal = useLotteryModal()
    const collect = useETL()
    const handleShare = useCallback(() => {
        const now = dayjs().unix()
        const {
            roll: { used },
        } = gameInfo
        collect?.("share_button", {
            button_name: gameType,
            game_result: gameResult?.result.id ?? "",
        })
        // 等级不足时不做抽奖
        if (!canRoll) {
            callShareModal()
            return
        }
        // 抽奖未使用过且活动未结束时进入抽奖流程
        if (!used && now < __ROGUE_5_LOTTERY_END_AT) {
            callShareModal(() => {
                callLotteryModal(() => {
                    if (needSignIn) setScene(EGameScene.Record)
                })
            })
        } else if (now < __ROGUE_5_SIGN_IN_END_AT) {
            // 签到活动未结束时进入签到记录页
            callShareModal(() => {
                if (needSignIn) setScene(EGameScene.Record)
            })
        } else {
            callShareModal()
        }
    }, [
        gameInfo,
        collect,
        gameType,
        gameResult?.result.id,
        canRoll,
        callShareModal,
        callLotteryModal,
        needSignIn,
        setScene,
    ])
    const [currentRelicKey, setCurrentRelicKey] = useState<string>("")
    const currentRelic = useMemo(() => {
        return RELIC_DICT[currentRelicKey]
    }, [currentRelicKey, RELIC_DICT])

    const isSignInEnd = useMemo(() => {
        return dayjs().unix() >= __ROGUE_5_SIGN_IN_END_AT
    }, [])

    const isFillEnd = useMemo(() => {
        return dayjs().unix() >= __ROGUE_5_LOTTERY_FILL_END_AT
    }, [])

    return (
        <div
            className={classNames(styles.sceneContainer, className)}
            style={style}
            ref={containerRef}
        >
            {src && (
                <motion.div
                    className={styles.resultContainer}
                    initial={{ opacity: 0, filter: "blur(10px)", scale: 0.9 }}
                    animate={{ opacity: 1, filter: "blur(0px)", scale: 1 }}
                    transition={{
                        duration: 0.8,
                        ease: "easeOut",
                        // cubic-bezier(0.1, 0.5, 0.0, 0.98)
                    }}
                >
                    <img className={styles.result} src={src} />
                </motion.div>
            )}
            {gameType === "rhodes" && (
                <div className={styles.relicInteraction}>
                    {gameRandomResult?.relics.map((r, index) => (
                        <div
                            key={`${r}-${index}`}
                            className={styles.item}
                            onMouseMove={() => {
                                setCurrentRelicKey(r)
                            }}
                            onMouseLeave={() => {
                                setCurrentRelicKey("")
                            }}
                            onClick={() => {
                                setCurrentRelicKey(r)
                            }}
                        ></div>
                    ))}
                </div>
            )}
            <div className={styles.buttonGroup}>
                <MainButton
                    className={classNames(
                        styles.button,
                        needSignIn && !isSignInEnd
                            ? undefined
                            : styles.textSmall,
                        gameInfo.roll.used || !canRoll || isFillEnd
                            ? undefined
                            : styles.smallSubTitle
                    )}
                    icon="box"
                    type="purple"
                    text={
                        needSignIn && !isSignInEnd
                            ? t("game.button.share.cn")
                            : t("game.button.share2.cn")
                    }
                    textEn={t("game.button.share.en")}
                    mark={"jie"}
                    subtitle={
                        gameInfo.roll.used || !canRoll || isFillEnd
                            ? ""
                            : t("game.button.share.subtitle")
                    }
                    onClick={handleShare}
                />
                {(isSignInEnd || !canRoll || !needSignIn) && (
                    <MainButton
                        className={styles.button}
                        icon="temple"
                        type="blue"
                        text={t("game.button.restart.cn")}
                        textEn={t("game.button.restart.en")}
                        mark={"jie"}
                        onClick={() => {
                            setScene(EGameScene.Loading)
                            resetGameResult()
                        }}
                    />
                )}
            </div>

            {gameType === "rhodes" && (
                <AnimatePresence mode="wait">
                    {currentRelic && (
                        <motion.div
                            key={currentRelic ? currentRelic.id : "empty"}
                            className={styles.relicFrame}
                            initial={{
                                opacity: 0,
                            }}
                            animate={{
                                opacity: 1,
                            }}
                            exit={{
                                opacity: 0,
                            }}
                            transition={{
                                duration: 0.3,
                                ease: "easeInOut",
                            }}
                        >
                            <div className={styles.title}>
                                {currentRelic.name}
                            </div>
                            <img
                                className={styles.image}
                                src={getRoguelikeItem(currentRelic.id)}
                            />
                            <div className={styles.usage}>
                                {currentRelic.usage}
                            </div>
                            <div
                                className={classNames(
                                    styles.desc,
                                    currentRelic.desc.length > 80 &&
                                        styles.small
                                )}
                            >
                                {currentRelic.desc}
                            </div>
                            {isMobile() && (
                                <div
                                    className={styles.close}
                                    onClick={() => {
                                        setCurrentRelicKey("")
                                    }}
                                ></div>
                            )}
                        </motion.div>
                    )}
                </AnimatePresence>
            )}
            <BackButton
                className={classNames(
                    styles.backButton,
                    !showButton && styles.hidden
                )}
                onClick={() => {
                    setScene(EGameScene.Game)
                }}
            />
        </div>
    )
}

export default GameSceneResult
